import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, type ActionMenuItem, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import { getActiveDineOutletName } from 'auth/dineOutlet';
import type { DineBillingPoint } from 'api/types';
import DineOpsBillingPointFormModal from 'components/modals/modules/dineops-module/DineOpsBillingPointFormModal';

/**
 * Billing Points (DO-313, spec 2.40 §12.1a) — the cash counters of the outlet
 * in the header. A waiter's bill goes to the counter of its table's section;
 * sections without one, room service, takeaway and counter orders go to the
 * default. A cashier opens a shift at a counter and sees only its bills.
 * A shared counter (S1) also serves other outlets: listed here with what it
 * serves, and under each outlet it serves as "Shared from …", read-only there.
 *
 * An outlet has a handful of counters, so the list is read whole and searched
 * in the browser.
 */

interface Filters { status?: string; role?: string }

const DineOpsBillingPointsScreen: React.FC<{ allowedActions?: string[] }> = ({ allowedActions = [] }) => {
  const outletName = useMemo(() => getActiveDineOutletName() || 'this outlet', []);
  const [rows, setRows] = useState<DineBillingPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [form, setForm] = useState<{ mode: 'create' | 'edit'; record: DineBillingPoint | null } | null>(null);
  const [deleting, setDeleting] = useState<DineBillingPoint | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const canAdd = allowedActions.includes('add') || allowedActions.includes('create');
  const canEdit = allowedActions.includes('edit') || allowedActions.includes('update');
  const canDelete = allowedActions.includes('delete');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try { setRows(await dineOpsService.getBillingPoints(true)); }
    catch (err: any) { setLoadError(err?.response?.data?.message ?? 'The counters could not be loaded.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !`${r.billingPointCode} ${r.billingPointName} ${r.sections ?? ''} ${r.printerName ?? ''}`.toLowerCase().includes(q)) return false;
      if (filters.status === 'active' && !r.isActive) return false;
      if (filters.status === 'inactive' && r.isActive) return false;
      if (filters.role === 'default' && !r.isDefault) return false;
      if (filters.role === 'other' && r.isDefault) return false;
      return true;
    });
  }, [rows, searchQuery, filters]);

  const handleExport = useCallback(() => {
    if (filtered.length === 0) { notificationService.toast.warning('No data to export'); return; }
    const headers = ['Code', 'Counter', 'Default', 'Shared from', 'Also serves', 'Sections', 'Printer', 'Shift open by', 'Unpaid bills', 'Status'];
    const lines = filtered.map((r) => [r.billingPointCode, r.billingPointName, r.isDefault ? 'Yes' : 'No',
      r.isShared ? r.homeOutletName ?? '' : '', r.isShared ? '' : r.servedOutletNames ?? '', r.sections ?? '', r.printerName ?? '',
      r.openShiftBy ?? '', r.openBillCount, r.isActive ? 'Active' : 'Inactive'].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const url = URL.createObjectURL(new Blob([[headers.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `billing-points-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }, [filtered]);

  const handleDelete = useCallback(async () => {
    if (!deleting) return;
    setIsDeleting(true);
    try {
      await dineOpsService.deleteBillingPoint(deleting.id, deleting.rowVersion);
      notificationService.toast.success(`"${deleting.billingPointName}" deleted.`);
      setDeleting(null);
      void load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'The counter could not be deleted.');
      setDeleting(null);
    } finally { setIsDeleting(false); }
  }, [deleting, load]);

  const columns = useMemo((): SyutGridColumn<DineBillingPoint>[] => [
    { id: 'billingPointCode', header: 'Code', accessorKey: 'billingPointCode', enableSorting: true, size: 90 },
    { id: 'billingPointName', header: 'Counter', accessorKey: 'billingPointName', enableSorting: true, size: 200,
      cell: ({ row }) => {
        const r = row.original;
        const chip = (text: string) => <span style={{ marginLeft: 6, fontSize: 11, padding: '1px 7px', borderRadius: 10, background: 'var(--bg-secondary)' }}>{text}</span>;
        return <span>{r.billingPointName}{r.isShared ? chip(`shared from ${r.homeOutletName ?? 'another outlet'}`) : r.isDefault && chip('default')}</span>;
      } },
    { id: 'sections', header: 'Sections billed here', accessorKey: 'sections', enableSorting: true, size: 240,
      cell: ({ row }) => (row.original.isShared
        ? <span style={{ color: 'var(--text-muted)' }}>every bill of {outletName} with no section counter</span>
        : row.original.sections ?? <span style={{ color: 'var(--text-muted)' }}>{row.original.isDefault ? 'room service, takeaway, counter' : 'none yet'}</span>) },
    { id: 'servedOutletNames', header: 'Also serves', accessorKey: 'servedOutletNames', enableSorting: true, size: 180,
      cell: ({ row }) => (row.original.isShared ? '—' : row.original.servedOutletNames ?? '—') },
    { id: 'printerName', header: 'Printer', accessorKey: 'printerName', enableSorting: true, size: 140, cell: ({ row }) => row.original.printerName ?? '—' },
    { id: 'openShiftBy', header: 'Shift open by', accessorKey: 'openShiftBy', enableSorting: true, size: 150, cell: ({ row }) => row.original.openShiftBy ?? '—' },
    { id: 'openBillCount', header: 'Unpaid bills', accessorKey: 'openBillCount', enableSorting: true, size: 110 },
    { id: 'isActive', header: 'Status', accessorKey: 'isActive', enableSorting: true, size: 100, cell: ({ row }) => (row.original.isActive ? 'Active' : 'Inactive') },
  ], [outletName]);

  const getRowActions = useCallback((row: DineBillingPoint): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    // A shared counter is managed at its home outlet.
    if (row.isShared) return [{ id: 'home', label: `Managed at ${row.homeOutletName ?? 'its home outlet'}`, disabled: true, onClick: () => undefined,
      description: 'Switch the working outlet to its home outlet to change it.' }];
    if (canEdit) items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => setForm({ mode: 'edit', record: row }) });
    if (canDelete && !row.isDefault) items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => setDeleting(row), variant: 'danger' });
    return items;
  }, [canEdit, canDelete]);

  const actionButtons: ActionButton[] = useMemo(() => {
    const b: ActionButton[] = [];
    if (canAdd) b.push({ id: 'add', icon: <IconSystem.add size={18} />, label: '+', onClick: () => setForm({ mode: 'create', record: null }), tooltip: 'Add Counter', variant: 'icon' });
    b.push({ id: 'refresh', icon: <IconSystem.refresh size={18} />, label: '', onClick: () => void load(), tooltip: 'Refresh data', variant: 'icon' });
    b.push({ id: 'export', icon: <IconSystem.download size={18} />, label: '', onClick: handleExport, tooltip: 'Export to CSV', variant: 'icon' });
    return b;
  }, [canAdd, load, handleExport]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Billing Points"
        description={`Cash counters of ${outletName}. A bill goes to its table's section's counter, else the default`}
        icon={<IconSystem.creditCard size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search code, counter, section or printer' }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, tooltip: 'Toggle filters' }}
        actions={actionButtons}
      />
      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <SyutGrid data={filtered} columns={columns} enableSorting enablePagination defaultPageSize={25} pageSizeOptions={[10, 25, 50, 100]}
              frozenRightColumns={['actions']} isLoading={loading} rowActions={getRowActions} emptyMessage={loadError ?? 'No counters found.'} />
          </div>
        </div>
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={16} /></button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect label="Status" options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
                value={filters.status ?? ''} onChange={(v) => setFilters((p) => ({ ...p, status: (v as string) || undefined }))} placeholder="All" size="sm" isClearable />
            </div>
            <div className="filter-section">
              <SyutSelect label="Default" options={[{ value: 'default', label: 'Default counter' }, { value: 'other', label: 'Other counters' }]}
                value={filters.role ?? ''} onChange={(v) => setFilters((p) => ({ ...p, role: (v as string) || undefined }))} placeholder="All" size="sm" isClearable />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => setFilters({})}>Clear</button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
          </div>
        </div>
      </div>

      <DineOpsBillingPointFormModal isOpen={!!form} mode={form?.mode ?? 'create'} record={form?.record} outletName={outletName}
        onClose={() => setForm(null)} onSaved={() => void load()} />
      <ConfirmDialog open={!!deleting} title="Delete counter?"
        message={`Delete "${deleting?.billingPointName ?? ''}"? It must have no sections, no open shift and no unpaid bills.`}
        confirmLabel="Delete" cancelLabel="Keep it" variant="danger" loading={isDeleting} loadingMessage="Deleting..."
        onConfirm={() => void handleDelete()} onCancel={() => setDeleting(null)} />
    </div>
  );
};

export default DineOpsBillingPointsScreen;
